import { ButtonLink, EmptyState, ExplainerBanner, MessageBubble, MessageDayDivider, Skeleton } from '@/ds'
import { NotAvailable } from '@/app/components/NotAvailable'
import { useLoadedStay } from '@/app/stay/StayProvider'
import { locale, time } from '@/lib/format'
import { whatsappHref } from '@/lib/whatsapp'
import { plainText } from '@/lib/text'
import { asyncValue, useAsync } from '@/lib/useAsync'
import { ayudaService } from '@/services/ayuda'
import type { ConversationMessage } from '@/services/ayuda'
import { SectionError } from '../Estancia/sections/SectionError'
import { BackLink } from '../Pagos/shared'
import { useLang } from '../Reserva/shared'
import { contactOf, HELP_ROUTES } from './shared'

/** "Lunes 14 de septiembre" */
function dayLabel(iso: string, lang: string): string {
  const text = new Intl.DateTimeFormat(locale(lang), { weekday: 'long', day: 'numeric', month: 'long' }).format(new Date(iso)).replace(',', '')
  return text.charAt(0).toLocaleUpperCase(lang) + text.slice(1)
}

const dayKey = (iso: string) => {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

/**
 * 8.9 · Conversación con la propiedad (Servicios.dc.html history, Bloque 6 §6g): lo dicho por WhatsApp y email en
 * solo lectura, sin chat propio. Para escribir, WhatsApp con la referencia. Sin endpoint en la API actual (D51).
 */
export function Conversacion() {
  const { t, lang } = useLang()
  const stay = useLoadedStay()
  const [data, reload] = useAsync(`ayuda-conversacion:${stay.guestCode}`, (signal) => ayudaService.getConversation(stay, signal))
  const [contactData] = useAsync(`ayuda-contacto:${stay.guestCode}`, (signal) => ayudaService.getContact(stay, signal))
  const contact = asyncValue(contactData) ?? contactOf(stay)
  const messages = [...(asyncValue(data) ?? [])].sort((a, b) => a.sentAt.localeCompare(b.sentAt))
  const groups = messages.reduce<{ key: string; label: string; items: ConversationMessage[] }[]>((acc, m) => {
    const key = dayKey(m.sentAt)
    const last = acc[acc.length - 1]
    if (last && last.key === key) last.items.push(m)
    else acc.push({ key, label: dayLabel(m.sentAt, lang), items: [m] })
    return acc
  }, [])

  const whatsapp = contact.whatsapp ? (
    <>
      <ButtonLink icon="message-circle" href={whatsappHref(contact.whatsapp, contact.reference)} target="_blank" rel="noreferrer">
        {t('v2.ds.contact.whatsapp')}
      </ButtonLink>
      <p className="pagos__note ayuda__center">{t('v2.ayuda.whatsappNota', { reference: contact.reference })}</p>
    </>
  ) : null

  return (
    <main className="page">
      <div className="pagos__head">
        <BackLink to={HELP_ROUTES.home} label={t('v2.peticiones.nueva.volver')} />
        <h1>{t('v2.ayuda.conversacion.titulo')}</h1>
        {data.kind !== 'unsupported' && <p className="page__lead">{t('v2.ayuda.conversacion.lead')}</p>}
      </div>
      {data.kind === 'loading' && (
        <div aria-busy="true">
          <Skeleton height={320} />
        </div>
      )}
      {data.kind === 'error' && <SectionError code={data.code} onRetry={reload} />}
      {data.kind === 'unsupported' && <NotAvailable />}
      {data.kind === 'ready' && messages.length === 0 && (
        <EmptyState icon="message-circle" title={t('v2.ayuda.conversacion.vacioTitulo')} headingLevel={2}>
          {t('v2.ayuda.conversacion.vacioTexto')}
        </EmptyState>
      )}
      {messages.length > 0 && (
        <>
          <ExplainerBanner>{t('v2.ayuda.conversacion.aviso')}</ExplainerBanner>
          <ol className="ayuda__messages ayuda__list" aria-label={t('v2.ayuda.conversacion.titulo')}>
            {groups.map((g) => (
              <li key={g.key} className="ayuda__messages">
                <MessageDayDivider>{g.label}</MessageDayDivider>
                {g.items.map((m) => (
                  <MessageBubble key={m.id} from={m.from} {...(m.author ? { author: m.author } : {})} channel={t(`v2.ayuda.conversacion.canal.${m.channel}`)} time={time(m.sentAt, lang)}>
                    {plainText(m.text)}
                  </MessageBubble>
                ))}
              </li>
            ))}
          </ol>
        </>
      )}
      {data.kind !== 'loading' && data.kind !== 'unsupported' && whatsapp}
    </main>
  )
}
